import { useState, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTenant } from "@/hooks/useTenant";
import { useFirmMetrics } from "@/hooks/useFirmMetrics";
import {
  Network,
  Search,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Building2,
  Filter,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { InvestmentDetailDrawer } from "@/components/firm/InvestmentDetailDrawer";

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export default function GPExposureView() {
  const navigate = useNavigate();
  const { isMFO, isLoading: tenantLoading } = useTenant();
  const { crossExposure, topGPs, isLoading: metricsLoading } = useFirmMetrics();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"commitment" | "nav" | "families">("commitment");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [gpFilter, setGpFilter] = useState<string>("all");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedInvestment, setSelectedInvestment] = useState<{
    instrumentId: string;
  } | null>(null);

  // Get unique GPs for filter - MUST be before any conditional returns (React hooks rules)
  const uniqueGPs = useMemo(() => {
    if (!crossExposure) return [];
    const gps = new Set(crossExposure.map((e) => e.gp_name).filter(Boolean));
    return Array.from(gps).sort() as string[];
  }, [crossExposure]);

  // Filter and sort exposure data
  const filteredExposure = useMemo(() => {
    if (!crossExposure) return [];

    let result = [...crossExposure];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.fund_name.toLowerCase().includes(q) ||
          (e.gp_name && e.gp_name.toLowerCase().includes(q))
      );
    }

    // GP filter
    if (gpFilter !== "all") {
      result = result.filter((e) => e.gp_name === gpFilter);
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === "commitment") {
        cmp = a.total_commitment_base - b.total_commitment_base;
      } else if (sortField === "nav") {
        cmp = a.total_nav_base - b.total_nav_base;
      } else if (sortField === "families") {
        cmp = a.family_count - b.family_count;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });

    return result;
  }, [crossExposure, searchQuery, gpFilter, sortField, sortDir]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const toggleRow = (id: string) => {
    const next = new Set(expandedRows);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedRows(next);
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredExposure.reduce(
      (acc, e) => ({
        commitment: acc.commitment + e.total_commitment_base,
        nav: acc.nav + e.total_nav_base,
        funds: acc.funds + 1,
      }),
      { commitment: 0, nav: 0, funds: 0 }
    );
  }, [filteredExposure]);

  const isLoading = tenantLoading || metricsLoading;

  // Redirect SFO users (AFTER all hooks)
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Network className="h-6 w-6" />
            Cross-Family GP Exposure
          </h1>
          <p className="text-muted-foreground">
            Analyze fund investments shared across families for GP relationship leverage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <Building2 className="h-3 w-3" />
            {totals.funds} Funds
          </Badge>
          <Badge variant="outline" className="gap-1">
            Total Commitment: {formatCurrency(totals.commitment)}
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search funds or GPs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={gpFilter} onValueChange={setGpFilter}>
          <SelectTrigger className="w-[200px]">
            <Filter className="h-4 w-4" />
            <SelectValue placeholder="Filter by GP" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All GPs</SelectItem>
            {uniqueGPs.map((gp) => (
              <SelectItem key={gp} value={gp}>
                {gp}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Top GPs Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {topGPs.slice(0, 5).map((gp, i) => (
          <Card
            key={gp.name}
            className="cursor-pointer hover:border-primary transition-colors"
            onClick={() => setGpFilter(gp.name)}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium truncate" title={gp.name}>
                {gp.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{formatCurrency(gp.totalCommitment)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {gp.fundCount} funds · {gp.familyCount} families
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Exposure Table */}
      <Card>
        <CardHeader>
          <CardTitle>Fund Exposure Details</CardTitle>
          <CardDescription>
            Click a row to expand and see which families are invested
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Fund Name</TableHead>
                <TableHead>GP</TableHead>
                <TableHead>Strategy</TableHead>
                <TableHead>Vintage</TableHead>
                <TableHead
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("commitment")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Total Commitment
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("nav")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Total NAV
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="text-right">Called</TableHead>
                <TableHead className="text-right">DPI</TableHead>
                <TableHead className="text-right">TVPI</TableHead>
                <TableHead
                  className="text-center cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("families")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Families
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExposure.map((fund) => {
                const isExpanded = expandedRows.has(fund.id);

                return (
                  <Collapsible key={fund.id} open={isExpanded} asChild>
                    <>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleRow(fund.id)}
                      >
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell
                          className="font-medium text-primary cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInvestment({ instrumentId: fund.instrument_id });
                          }}
                        >
                          {fund.fund_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {fund.gp_name || "-"}
                        </TableCell>
                        <TableCell>
                          {fund.strategy && (
                            <Badge variant="outline" className="text-xs">
                              {fund.strategy}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{fund.vintage_year || "-"}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(fund.total_commitment_base)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(fund.total_nav_base)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(fund.total_paid_in_base)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {fund.total_paid_in_base > 0 ? '-' : '-'}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {fund.total_paid_in_base > 0
                            ? `${(fund.total_nav_base / fund.total_paid_in_base).toFixed(2)}x`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant={fund.family_count >= 2 ? "default" : "secondary"}
                          >
                            {fund.family_count}
                          </Badge>
                        </TableCell>
                      </TableRow>

                      <CollapsibleContent asChild>
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={11} className="p-0">
                            <div className="p-4 pl-12">
                              <h4 className="text-sm font-medium mb-3">Family Breakdown</h4>
                              <div className="space-y-2">
                                {fund.family_exposures?.map((fe, i) => (
                                  <div
                                    key={i}
                                    className="flex items-center gap-6 p-3 rounded-lg bg-background border cursor-pointer hover:border-primary transition-colors group"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (fe.family_id) {
                                        navigate(`/firm/families/${fe.family_id}`);
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-3 flex-1">
                                      <div>
                                        <div className="font-medium text-sm flex items-center gap-2">
                                          {fe.family_name || "Unknown Family"}
                                          <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Commitment: {formatCurrency(fe.commitment || 0)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <div className="font-medium">
                                        {formatCurrency(fe.nav || 0)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">NAV</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                );
              })}
              {filteredExposure.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    No fund exposure data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Investment Detail Drawer */}
      <InvestmentDetailDrawer
        open={!!selectedInvestment}
        onOpenChange={(open) => !open && setSelectedInvestment(null)}
        instrumentId={selectedInvestment?.instrumentId || null}
      />
    </div>
  );
}
